import { useApolloClient } from '@apollo/client';
import { IonIcon, IonItem, IonLabel, IonList, IonPopover } from '@ionic/react';
import { Auth } from 'aws-amplify';
import Avatar from 'components/Avatar';
import { useIdentityUser } from 'hooks/useIdentityUser';
import { useWorkspace } from 'hooks/useWorkspace';
import { chevronForwardOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';

const useStyles = createUseStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '13px',
    padding: '14px',
  },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 10px',
    margin: '0 -1px',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor: 'rgba(var(--ion-color-dark-rgb), 0.055)',
    },
    '&:hover $avatar': {
      filter: 'brightness(0.92)',
      boxShadow: 'inset 0 0 0 1px var(--ion-color-medium)',
    },
  },
  popover: {
    '--width': '200px',
  },
  popoverList: {
    padding: 0,
  },
  optionLabel: {
    fontWeight: 500,
    fontSize: 13,
  },
  danger: {
    '--color': 'var(--ion-color-danger)',
  },
  user: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    minWidth: 0,
    width: '100%',
  },
  avatar: {
    '--border-radius': '12px',
    width: 36,
    height: 36,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'ui-monospace, Menlo, monospace',
    flexShrink: 0,
    transition: 'filter 120ms ease, box-shadow 120ms ease',
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.1,
    fontWeight: 600,
    color: 'var(--ion-color-dark)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  email: {
    margin: 0,
    marginTop: 4,
    fontSize: '11.5px',
    color: 'var(--ion-color-medium)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chevron: {
    fontSize: 14,
    color: 'rgba(var(--ion-color-dark-rgb), 0.45)',
    flexShrink: 0,
  },
});

const MenuBottomPanel: React.FC = () => {
  const classes = useStyles();
  const user = useIdentityUser();
  const { workspace, resetWorkspace } = useWorkspace();
  const { resetStore } = useApolloClient();
  const history = useHistory();
  const [popupState, setPopupState] = useState<{ open: boolean; event?: any }>({ open: false });

  const signOut = async () => {
    await Auth.signOut();
    await resetStore();
    resetWorkspace();
  };

  const openMenu = (event: any) => {
    event.persist();
    setPopupState({ open: true, event });
  };

  const closeMenu = () => setPopupState({ open: false });

  const goToAccount = () => {
    closeMenu();
    history.push(`/accounts/manage/${workspace.worker.id}`);
  };

  const logOut = () => {
    closeMenu();
    signOut();
  };

  return (
    <div className={classes.panel}>
      <div className={classes.item} onClick={openMenu}>
        <div className={classes.user}>
          <Avatar
            alt={user.name}
            bgColor="var(--ion-color-light)"
            color="var(--ion-color-medium-shade)"
            className={classes.avatar}
          />
          <div className={classes.textBlock}>
            <p className={classes.name}>{user.name}</p>
            <p className={classes.email}>{user.email}</p>
          </div>
          <IonIcon icon={chevronForwardOutline} className={classes.chevron} />
        </div>
      </div>
      <IonPopover
        isOpen={popupState.open}
        event={popupState.event}
        onDidDismiss={closeMenu}
        className={classes.popover}
        showBackdrop={false}
      >
        <IonList className={classes.popoverList}>
          <IonItem button={true} detail={false} lines="none" onClick={goToAccount}>
            <IonLabel className={classes.optionLabel}>Go to account</IonLabel>
          </IonItem>
          <IonItem button={true} detail={false} lines="none" className={classes.danger} onClick={logOut}>
            <IonLabel className={classes.optionLabel}>Log out</IonLabel>
          </IonItem>
        </IonList>
      </IonPopover>
    </div>
  );
};

export default MenuBottomPanel;
